import { type Object3D, type Scene, Vector3 } from 'three';
import type { Assets } from './assets';

const H = 'kaykit/halloween/';
/** Kawałki szkieleta: czaszka, żebra, kości (modele z dekoracji cmentarza). */
const PIECES = [
  { path: H + 'skull.gltf', scale: 0.42 },
  { path: H + 'bone_A.gltf', scale: 0.55 },
  { path: H + 'bone_B.gltf', scale: 0.55 },
  { path: H + 'bone_C.gltf', scale: 0.5 },
  { path: H + 'ribcage.gltf', scale: 0.5 },
];
const MAX_PIECES = 70;
const GRAVITY = 22;
const LIFE = 2.4;

interface Piece {
  obj: Object3D;
  kind: number;
  vel: Vector3;
  spin: Vector3;
  life: number;
  scale: number;
}

/**
 * Pokonany szkielet rozsypuje się: czaszka i kości wylatują w powietrze, odbijają się od ziemi,
 * toczą i po chwili zapadają pod ziemię. Kierunek wyrzutu = od bohatera (siła ciosu).
 */
export class Debris {
  private readonly live: Piece[] = [];
  private readonly pool: Piece[] = [];

  constructor(
    private readonly scene: Scene,
    private readonly assets: Assets,
  ) {}

  burst(at: Vector3, away: Vector3, size: number, heavy: boolean): void {
    const count = heavy ? 6 : 3;
    for (let i = 0; i < count; i++) {
      // Pierwszy zawsze czaszka, potem kości; żebra tylko przy dużych.
      // Modulo dodatnie także dla ujemnych współrzędnych (inaczej indeks spoza listy).
      const kind = i === 0 ? 0 : heavy && i === 1 ? 4 : 1 + ((((i + Math.floor(at.x * 7)) % 3) + 3) % 3);
      const p = this.take(kind);
      if (!p) return;
      const spread = (Math.random() - 0.5) * 1.6;
      const dir = new Vector3(away.x + spread * -away.z, 0, away.z + spread * away.x).normalize();
      const speed = (heavy ? 4.5 : 3.5) * (0.6 + Math.random() * 0.6);
      p.vel.set(dir.x * speed, 4 + Math.random() * 3.5, dir.z * speed);
      p.spin.set((Math.random() - 0.5) * 16, (Math.random() - 0.5) * 12, (Math.random() - 0.5) * 16);
      p.life = LIFE * (0.8 + Math.random() * 0.4);
      p.scale = PIECES[kind]!.scale * size;
      p.obj.scale.setScalar(p.scale);
      p.obj.position.set(at.x, 0.9 * size + Math.random() * 0.4, at.z);
      p.obj.rotation.set(Math.random() * 6, Math.random() * 6, Math.random() * 6);
      this.scene.add(p.obj);
      this.live.push(p);
    }
  }

  private take(kind: number): Piece | null {
    const i = this.pool.findIndex((p) => p.kind === kind);
    if (i >= 0) return this.pool.splice(i, 1)[0]!;
    if (this.live.length + this.pool.length >= MAX_PIECES) {
      // Za dużo kawałków naraz — najstarszy znika od razu.
      const oldest = this.live.shift();
      if (!oldest) return null;
      oldest.obj.removeFromParent();
      if (oldest.kind === kind) return oldest;
      this.pool.push(oldest);
    }
    const obj = this.assets.template(PIECES[kind]!.path).scene.clone();
    obj.traverse((o) => (o.castShadow = true));
    return { obj, kind, vel: new Vector3(), spin: new Vector3(), life: 0, scale: 1 };
  }

  update(dt: number): void {
    for (let i = this.live.length - 1; i >= 0; i--) {
      const p = this.live[i]!;
      p.life -= dt;
      const o = p.obj;
      p.vel.y -= GRAVITY * dt;
      o.position.addScaledVector(p.vel, dt);
      o.rotation.x += p.spin.x * dt;
      o.rotation.y += p.spin.y * dt;
      o.rotation.z += p.spin.z * dt;
      const floor = 0.08 * p.scale;
      if (o.position.y < floor && p.life > 0.6) {
        // Odbicie: traci energię, obrót zwalnia — kawałek się toczy i zatrzymuje.
        o.position.y = floor;
        if (p.vel.y < 0) p.vel.y *= -0.35;
        p.vel.x *= 0.6;
        p.vel.z *= 0.6;
        p.spin.multiplyScalar(0.55);
        if (Math.abs(p.vel.y) < 0.6) p.vel.y = 0;
      }
      if (p.life < 0.6) {
        // Koniec: zapada się pod ziemię.
        p.vel.set(0, -0.6, 0);
        p.spin.multiplyScalar(0.9);
      }
      if (p.life <= 0) {
        o.removeFromParent();
        this.live.splice(i, 1);
        this.pool.push(p);
      }
    }
  }
}
